import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiX, FiHome, FiBook, FiFileText, FiMic, FiVideo, FiShare2, FiGlobe, FiImage, FiSearch } from 'react-icons/fi';
import { BiBookReader, BiNews } from 'react-icons/bi';
import { BsJournalText, BsCameraReels, BsNewspaper, BsBuilding } from 'react-icons/bs';
import { MdEvent, MdSchool } from 'react-icons/md';
import { AiOutlineRobot } from 'react-icons/ai';
import { FaWikipediaW } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const sourceTypes = [
  { path: '/', name: 'Inicio', icon: FiHome },
  { path: '/blog', name: 'Artículo de blog', icon: FiFileText, description: 'Entradas de blogs', keywords: ['blogging', 'entrada', 'entradas', 'publicación', 'publicacion', 'publicaciones', 'articulo', 'artículo', 'articulos', 'artículos', 'blog', 'blogs'] },
  { path: '/magazin', name: 'Artículo de magazín', icon: BiNews, description: 'Artículos de revistas de carácter general o culturales', keywords: ['magazin', 'magazine', 'magacín', 'magacines', 'magazines', 'magacine', 'magacin', 'revista', 'revistas', 'magazín', 'publicación', 'publicacion', 'publicaciones', 'articulo', 'artículo', 'articulos', 'artículos'] },
  { path: '/periodico', name: 'Artículo de periódico', icon: BsNewspaper, description: 'Artículos de prensa', keywords: ['periódico', 'periodico', 'periódicos', 'periodicos', 'prensa', 'diario', 'diarios', 'publicación', 'publicacion', 'publicaciones', 'articulo', 'artículo', 'articulos', 'artículos'] },
  { path: '/revista', name: 'Artículo de revista', icon: BsJournalText, description: 'Artículos de revistas académicas o científicas', keywords: ['revista', 'revistas', 'academica', 'académica', 'academicas', 'académicas', 'científica', 'cientifica', 'científicas', 'cientificas', 'publicación', 'publicacion', 'publicaciones', 'articulo', 'artículo', 'articulos', 'artículos'] },
  { path: '/wikipedia', name: 'Artículo de Wikipedia', icon: FaWikipediaW, description: 'Artículos de la enciclopedia Wikipedia', keywords: ['publicación', 'publicacion', 'publicaciones', 'articulo', 'artículo', 'articulos', 'artículos', 'wiki', 'enciclopedia', 'diccionario', 'entrada', 'entradas'] },
  { path: '/capitulo', name: 'Capítulo de libro', icon: BiBookReader, description: 'Capítulos dentro de libros editados', keywords: ['publicación', 'publicacion', 'publicaciones', 'capítulo', 'capitulo', 'capítulos', 'capitulos', 'libro', 'libros', 'obra', 'obras', 'editor', 'editora', 'editores', 'editoras', 'compilador', 'compiladores', 'compiladoras', 'coordinador', 'coordinadores', 'coordinadoras'] },
  { path: '/podcast', name: 'Episodio de pódcast', icon: FiMic, description: 'Episodios de pódcast', keywords: ['episodio', 'episodios', 'podcast', 'pódcast', 'podcasts', 'podcasting'] },
  { path: '/evento', name: 'Evento', icon: MdEvent, description: 'Conferencias, ponencias, pósteres', keywords: ['evento', 'eventos', 'ponencia', 'ponencias', 'conferencia', 'conferencias', 'póster', 'poster', 'pósteres', 'posteres', 'posters', 'pósters', 'seminario', 'congreso', 'encuentro', 'jornada', 'foro', 'taller', 'seminarios', 'congresos', 'encuentros', 'jornadas', 'foros', 'talleres'] },
  { path: '/libro', name: 'Libro', icon: FiBook, description: 'Libros completos', keywords: ['libro', 'libros', 'obra', 'obras'] },
  { path: '/llm', name: 'LLM (inteligencia artificial)', icon: AiOutlineRobot, description: 'Modelos de lenguaje grande, asistentes IA, chatbots', keywords: ['chatbot', 'chatbots', 'IA', 'AI', 'artificial intelligence', 'inteligencia artificial', 'llm', 'modelo de lenguaje grande', 'modelo de lenguaje extenso', 'asistente', 'asistentes', 'virtual', 'virtuales', 'digital', 'digitales', 'ChatGPT', 'chat gpt', 'gpt', 'llama', 'mistral', 'gemini', 'google', 'OpenAI', 'perplexity', 'grok', 'x', 'xAI', 'Microsoft', 'copilot', 'meta', 'anthropic', 'Amazon', 'nova', 'DeepSeek', 'Claude'] },
  { path: '/material-visual', name: 'Material visual', icon: FiImage, description: 'Fotografías, imágenes prediseñadas, banco de imágenes y otros trabajos visuales', keywords: ['obra', 'obras', 'arte', 'pintura', 'pinturas', 'escultura', 'esculturas', 'grabado', 'grabados', 'dibujo', 'dibujos', 'imagen', 'imágenes', 'imagenes', 'foto', 'fotografía', 'fotografia', 'fotografías', 'fotografias', 'caricatura', 'caricaturas', 'cómic', 'comic', 'cómics', 'comics', 'historieta', 'historietas', 'PowerPoint', 'Power Point', 'PPT', 'PPTX', 'PDF', 'XLS', 'XLSX', 'Excel', 'microsoft', 'word', 'DOC', 'DOCX', 'canva', 'vista', 'vistacreate', 'create', 'vista create', 'ícono', 'icono', 'íconos', 'iconos', 'mapa', 'mapas', 'logo', 'logos', 'iconografía', 'iconografia', 'iconografías', 'iconografias', 'infografía', 'infografia', 'infografías', 'infografias', 'diapositiva', 'diapositivas', 'visual', 'visuales', 'material', 'materiales', 'contenido', 'contenidos'] },
  { path: '/pelicula', name: 'Película, documental o video', icon: BsCameraReels, description: 'Contenido audiovisual', keywords: ['peli', 'película', 'pelicula', 'pelis', 'películas', 'peliculas', 'video', 'videos', 'documental', 'documentales', 'docu', 'docus', 'film', 'films', 'movie', 'movies'] },
  { path: '/redes-sociales', name: 'Redes sociales', icon: FiShare2, description: 'Medios sociales', keywords: ['YouTube', 'yt', 'you tube', 'Vimeo', 'TedTalk', 'Ted talk', 'TED', 'charla', 'insta', 'Instagram', 'Facebook', 'face', 'fb', 'TikTok', 'Tik Tok', 'X', 'Twitter', 'video', 'audio', 'audiovisual', 'videos', 'audios', 'audiovisuales', 'red', 'red social', 'medio', 'media', 'medios sociales', 'social media', 'contenido', 'contenidos', 'publicación', 'publicacion', 'publicaciones'] },
  { path: '/sitio-web', name: 'Sitio web', icon: FiGlobe, description: 'Sitios, portales y páginas web', keywords: ['sitio', 'sitios', 'página web', 'páginas web', 'pagina web', 'paginas web', 'sitios web', 'página', 'páginas', 'pagina', 'paginas', 'internet', 'www', 'world wide web', 'portal web', 'portales web', 'portal', 'portales'] },
  { path: '/tesis', name: 'Tesis y trabajos de grado', icon: MdSchool, description: 'Tesis de maestría y doctorado, trabajos de grado', keywords: ['tesis', 'maestría', 'master', 'máster', 'maestria', 'doctor', 'doctoral', 'doctorado', 'doctora', 'trabajo de grado', 'trabajos', 'trabajo', 'grado', 'trabajos de grado', 'monografía', 'monografia', 'monografías', 'monografias', 'disertación', 'disertacion', 'disertaciones', 'pregrado', 'pregrados', 'pre-grado', 'pre-grados', 'posgrado', 'posgrados', 'postgrado', 'postgrados', 'post-grado', 'post-grados', 'pos-grado', 'pos-grados'] }
];

function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // Filtrar tipos de fuentes basado en el término de búsqueda
  const filteredSourceTypes = sourceTypes.filter(type => {
    const searchLower = searchTerm.toLowerCase();

    // Buscar en nombre y descripción
    if (type.name.toLowerCase().includes(searchLower) ||
      (type.description && type.description.toLowerCase().includes(searchLower))) {
      return true;
    }

    // Buscar en palabras clave si están definidas
    if (type.keywords && type.keywords.some(keyword =>
      keyword.toLowerCase().includes(searchLower)
    )) {
      return true;
    }

    return false;
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm && !recentSearches.includes(searchTerm)) {
      setRecentSearches(prev => [searchTerm, ...prev].slice(0, 3));
    }
  };

  const handleItemClick = (path) => {
    // Cierra el sidebar en todos los dispositivos al hacer clic en un elemento
    onClose();
  };

  // Animation variants
  const sidebarVariants = {
    hidden: { x: "-100%", opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  const searchVariants = {
    initial: { scale: 1 },
    focused: {
      scale: 1.02,
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)"
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3
      }
    })
  };

  return (
    <>
      {/* Overlay para todos los dispositivos cuando el sidebar está abierto */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 flex flex-col transform transition-transform duration-300 ease-in-out dark:bg-gray-800 dark:text-gray-100 ${isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        initial="hidden"
        animate={isOpen ? "visible" : "hidden"}
        variants={sidebarVariants}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 font-['Poppins']">Tipos de fuentes</h2>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors dark:hover:bg-gray-700"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </motion.button>
        </div>

        {/* Barra de búsqueda */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0 dark:border-gray-700">
          <form onSubmit={handleSearchSubmit}>
            <motion.div
              className="relative"
              variants={searchVariants}
              initial="initial"
              animate={searchFocused ? "focused" : "initial"}
            >
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiSearch} className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar tipo de fuente"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
              {searchTerm && (
                <motion.button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={FiX} className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                </motion.button>
              )}
            </motion.div>
          </form>

          {/* Recent searches */}
          {recentSearches.length > 0 && !searchTerm && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1 dark:text-gray-400">Búsquedas recientes:</p>
              <div className="flex flex-wrap gap-1">
                {recentSearches.map((term, index) => (
                  <motion.button
                    key={index}
                    className="text-xs bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-1 dark:bg-gray-700 dark:hover:bg-gray-600"
                    onClick={() => setSearchTerm(term)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {term}
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lista de navegación con scroll */}
        <nav className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          <motion.ul className="space-y-1">
            {filteredSourceTypes.map((type, index) => (
              <motion.li
                key={type.path}
                custom={index}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <Link
                  to={type.path}
                  onClick={() => handleItemClick(type.path)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${location.pathname === type.path
                    ? 'bg-blue-50 text-blue-700 font-medium dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }`}
                >
                  <SafeIcon
                    icon={type.icon}
                    className={`w-5 h-5 flex-shrink-0 ${location.pathname === type.path
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-500 dark:text-gray-400'
                      }`}
                  />
                  <span className="text-sm font-['Poppins']">{type.name}</span>
                </Link>
              </motion.li>
            ))}

            {filteredSourceTypes.length === 0 && searchTerm && (
              <motion.li
                className="px-3 py-8 text-sm text-gray-500 text-center dark:text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex flex-col items-center gap-2">
                  <FiSearch className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                  <p>No se encontraron tipos de fuentes que coincidan con "{searchTerm}"</p>
                </div>
              </motion.li>
            )}
          </motion.ul>
        </nav>
      </motion.aside>
    </>
  );
}

export default Sidebar;